import { getImage } from '../../Common/Images/utilities/imageUtils.js';
import { loadProject } from '../../ProjectsAndSkills/Projects/utilities/projectsUtils.js';
import { loadSkill } from '../../ProjectsAndSkills/Skills/utilities/skillsUtils.js';
import {
  cloneEditorValue,
  createElementNode,
  createTextNode,
  getElementPath,
  isElementNode,
  isTextNode,
  normalizeClassList,
  validateEditorDocument,
} from './htmlEditorUtils.js';

export const PROJECTS_TAG = 'projects';
export const SKILLS_TAG = 'skills';

const classDefinition = (base = {}) => Object.freeze({ base: Object.freeze(base), sm: {}, md: {}, lg: {}, xl: {} });

export const COLLECTION_CLASS_DEFINITIONS = Object.freeze({
  'projects-collection': classDefinition({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 22rem), 1fr))',
    gridAutoRows: '1fr',
    alignItems: 'stretch',
    gap: '1.5rem',
    width: '100%',
  }),
  'project-item': classDefinition({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    minWidth: '0',
    height: '30rem',
    maxHeight: '30rem',
    padding: '1.25rem',
    backgroundColor: '#ffffff',
    border: '1px solid #dee2e6',
    borderRadius: '0.5rem',
    boxShadow: '0 0.25rem 0.75rem rgba(33, 37, 41, 0.08)',
    overflow: 'hidden',
    cursor: 'pointer',
  }),
  'project-title': classDefinition({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    overflow: 'hidden',
    margin: '0',
    color: '#212529',
    fontSize: 'clamp(1.35rem, 2vw, 1.75rem)',
    lineHeight: '1.2',
  }),
  'project-overview': classDefinition({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '4',
    overflow: 'hidden',
    margin: '0',
    color: '#495057',
    lineHeight: '1.6',
  }),
  'project-link': classDefinition({
    display: 'none',
  }),
  'project-skills': classDefinition({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.4rem',
    maxHeight: '2rem',
    overflow: 'hidden',
  }),
  'project-skill': classDefinition({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    minWidth: '0',
    padding: '0.3rem 0.55rem',
    color: '#212529',
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: '50rem',
    fontSize: '0.78rem',
    fontWeight: '600',
    lineHeight: '1',
    whiteSpace: 'nowrap',
  }),
  'project-skill-icon': classDefinition({
    display: 'inline-flex',
    width: '1.1rem',
    height: '1.1rem',
    minWidth: '1.1rem',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '0.2rem',
    objectFit: 'cover',
    fontSize: '0.85rem',
  }),
  'project-detail-hint': classDefinition({
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: '0.4rem',
    marginTop: 'auto',
    color: '#0d6efd',
    fontWeight: '600',
  }),
  'project-images': classDefinition({
    position: 'relative',
    display: 'block',
    width: '100%',
    height: '13rem',
    minHeight: '13rem',
    minWidth: '0',
    overflow: 'hidden',
    backgroundColor: '#f8f9fa',
    borderRadius: '0.375rem',
  }),
  'project-carousel-track': classDefinition({
    width: '100%',
    height: '100%',
  }),
  'project-image': classDefinition({
    display: 'flex',
    flexDirection: 'column',
    minWidth: '0',
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    margin: '0',
    overflow: 'hidden',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '0.375rem',
  }),
  'project-image-media': classDefinition({
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    minHeight: '0',
    objectFit: 'cover',
  }),
  'project-image-caption': classDefinition({
    position: 'absolute',
    right: '0',
    bottom: '0',
    left: '0',
    padding: '0.5rem 0.65rem',
    overflow: 'hidden',
    color: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.68)',
    fontSize: '0.8rem',
    lineHeight: '1.35',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
  'project-image-placeholder': classDefinition({
    display: 'grid',
    width: '100%',
    height: '100%',
    placeItems: 'center',
    color: '#6c757d',
    backgroundColor: '#f8f9fa',
  }),
  'skills-collection': classDefinition({
    display: 'block',
    width: '100%',
  }),
  'skill-item': classDefinition({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.65rem',
    minWidth: '0',
    height: '100%',
    padding: '1.25rem',
    backgroundColor: '#ffffff',
    border: '1px solid #dee2e6',
    borderTop: '0.25rem solid #198754',
    borderRadius: '0.5rem',
    boxShadow: '0 0.25rem 0.75rem rgba(33, 37, 41, 0.08)',
    overflow: 'hidden',
  }),
  'skill-icon': classDefinition({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '3rem',
    height: '3rem',
    minWidth: '3rem',
    borderRadius: '0.5rem',
    objectFit: 'cover',
    backgroundColor: '#f8f9fa',
    fontSize: '1.65rem',
  }),
  'skill-rating': classDefinition({
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem',
    minHeight: '1rem',
    color: '#e0a800',
    fontSize: '0.9rem',
    lineHeight: '1',
  }),
  'skill-name': classDefinition({
    margin: '0',
    color: '#212529',
    fontSize: '1.15rem',
    fontWeight: '700',
    lineHeight: '1.3',
  }),
  'skill-description': classDefinition({
    margin: '0',
    color: '#6c757d',
    fontSize: '0.95rem',
    lineHeight: '1.6',
  }),
});

const LEGACY_PROJECT_BASE_VALUES = Object.freeze({
  'project-item': { height: '100%' },
  'project-link': { display: 'inline-flex' },
  'project-images': { display: 'grid' },
  'project-image-media': { height: 'auto' },
  'project-image-caption': { color: '#6c757d' },
});

const text = (content) => createTextNode(String(content || ''));
const element = (type, attributes = {}, children = []) => createElementNode(type, attributes, children);

const normalizeRecordId = (value) => String(value || '').trim();
const PROJECT_FIELDS = new Set([
  'title', 'overview', 'project-url', 'skills', 'skill', 'skill-icon', 'image', 'image-caption',
]);
const SKILL_FIELDS = new Set(['icon', 'rating', 'name', 'description']);
const MANAGED_ROLES = new Set([
  'projects-collection', 'project-item', 'project-images', 'project-carousel-track',
  'project-carousel-control', 'project-carousel-control-icon', 'project-image',
  'project-image-placeholder', 'project-skills', 'project-skill', 'project-skill-icon',
  'project-detail-hint', 'project-detail-icon',
  'skills-collection', 'skills-row', 'skill-item', 'skill-item-content', 'skill-icon', 'rating-star',
]);
const MANAGED_DECORATION_ROLES = new Set([
  'project-carousel-control',
  'project-carousel-control-icon',
  'project-skill-icon',
  'project-detail-hint',
  'project-detail-icon',
]);

export const isCollectionNode = (node) => (
  isElementNode(node) && (node.type === PROJECTS_TAG || node.type === SKILLS_TAG)
);

export const containsCollectionNode = (node) => Boolean(
  node && (isCollectionNode(node) || (!isTextNode(node) && (node.children || []).some(containsCollectionNode)))
);

export const getCollectionKind = (node) => {
  if (node?.type === PROJECTS_TAG) return 'project';
  if (node?.type === SKILLS_TAG) return 'skill';
  return null;
};

export const getBoundRecordId = (node, kind = null) => {
  const resolvedKind = kind || (node?.attributes?.['data-project-id'] ? 'project' : node?.attributes?.['data-skill-id'] ? 'skill' : null);
  return normalizeRecordId(node?.attributes?.[`data-${resolvedKind}-id`]);
};

const isSkillsRow = (node) => (
  isElementNode(node)
  && node.type === 'div'
  && node.attributes?.['data-managed-role'] === 'skills-row'
);

const getCollectionItems = (node) => {
  if (getCollectionKind(node) !== 'skill') return node?.children || [];
  const row = (node.children || []).find(isSkillsRow);
  // Flat skill collections are retained until hydration upgrades their structure.
  return row ? row.children || [] : node.children || [];
};

const replaceCollectionItems = (node, items) => {
  if (getCollectionKind(node) !== 'skill') return { ...node, children: items };
  const previousRow = (node.children || []).find(isSkillsRow);
  const row = previousRow
    ? { ...previousRow, children: items }
    : element('div', { class: ['row', 'g-3'], 'data-managed-role': 'skills-row' }, items);
  return { ...node, children: [row] };
};

export const getCollectionRecordIds = (node) => {
  const kind = getCollectionKind(node);
  if (!kind) return [];

  return [...new Set(getCollectionItems(node).map((child) => getBoundRecordId(child, kind)).filter(Boolean))];
};

export const getOwningCollectionNode = (root, nodeId) => {
  const path = getElementPath(root, nodeId);
  return [...path].reverse().find(isCollectionNode) || null;
};

export const isManagedCollectionNode = (node) => Boolean(
  isCollectionNode(node) ||
  node?.attributes?.['data-managed-role'] ||
  node?.attributes?.['data-project-field'] ||
  node?.attributes?.['data-skill-field'] ||
  node?.attributes?.['data-project-id'] ||
  node?.attributes?.['data-skill-id']
);

export const isManagedSourceNode = (node) => Boolean(
  node?.attributes?.['data-project-field'] ||
  node?.attributes?.['data-skill-field'] ||
  node?.attributes?.['data-managed-role']
);

export const isManagedEditorDecoration = (node) => (
  MANAGED_DECORATION_ROLES.has(node?.attributes?.['data-managed-role'])
);

export const getManagedNodeCapabilities = (node) => {
  if (isManagedEditorDecoration(node)) {
    return {
      canAddChild: false,
      canDelete: false,
      canDuplicate: false,
      canInsertSibling: false,
      canEditSource: false,
      canManageRecords: false,
      canMove: false,
      canChangeLayer: false,
      canStyle: false,
    };
  }

  const managed = isManagedCollectionNode(node);
  const role = node?.attributes?.['data-managed-role'];
  const managedItem = role === 'project-item' || role === 'skill-item';
  const collection = isCollectionNode(node);
  const managedInternal = managed && !collection && !managedItem;

  return {
    canAddChild: !managed,
    canDelete: !managedInternal,
    canDuplicate: !managed,
    canInsertSibling: !managedInternal && !managedItem,
    canEditSource: !managed,
    canManageRecords: collection,
    canMove: !managedInternal,
    canChangeLayer: !managed || collection,
    canStyle: !isTextNode(node),
  };
};

export const getCollectionMenuAvailability = (disallowedItems = [], userId = null) => {
  const blockedItems = new Set(disallowedItems.map((item) => String(item || '').toLowerCase()));
  const projects = Boolean(userId) && !blockedItems.has('project') && !blockedItems.has('projects');
  const skills = Boolean(userId) && !blockedItems.has('skill') && !blockedItems.has('skills');
  return { projects, skills, dataTab: projects || skills };
};

const createProjectImageNode = (image, imageData = null, active = false) => {
  const imageId = normalizeRecordId(typeof image === 'string' ? image : image?.imageId || image?.id);
  const src = imageData?.base64 || image?.base64 || image?.src || image?.url || '';
  const description = image?.description || imageData?.description || imageData?.alt || imageData?.filename || 'Project image';

  return element('figure', {
    class: ['project-image', 'carousel-item', active ? 'active' : ''],
    'data-managed-role': 'project-image',
    ...(imageId ? { 'data-image-id': imageId } : {}),
  }, [
    element('img', {
      class: ['img-fluid', 'project-image-media'],
      src,
      alt: description,
      'data-project-field': 'image',
      ...(imageId ? { 'data-image-id': imageId } : {}),
    }),
    element('figcaption', {
      class: ['project-image-caption'],
      'data-project-field': 'image-caption',
    }, [text(description)]),
  ]);
};

const projectCarouselId = (projectId, stableNodeId = '') => {
  const safeId = `${projectId || 'project'}-${stableNodeId}`
    .replace(/[^A-Za-z0-9_-]/g, '-');
  return `project-carousel-${safeId}`;
};

const createProjectCarouselControl = (carouselId, direction) => element('a', {
  class: [`carousel-control-${direction}`],
  href: `#${carouselId}`,
  role: 'button',
  'aria-label': direction === 'prev' ? 'Previous project image' : 'Next project image',
  'data-bs-slide': direction,
  'data-project-carousel-control': 'true',
  'data-managed-role': 'project-carousel-control',
}, [
  element('span', {
    class: [`carousel-control-${direction}-icon`],
    'aria-hidden': true,
    'data-managed-role': 'project-carousel-control-icon',
  }),
]);

const projectSkillIds = (project) => [...new Set(
  (Array.isArray(project?.skillsUsed) ? project.skillsUsed : [])
    .map(normalizeRecordId)
    .filter(Boolean)
)];

const createProjectSkillNode = (skill, iconImagesById = new Map()) => {
  const skillId = normalizeRecordId(skill?.id);
  const icon = skill?.icon;
  const imageId = icon?.type === 'image' ? normalizeRecordId(icon.imageId) : '';
  const imageSrc = imageId ? iconImagesById.get(imageId)?.base64 || '' : '';
  const bootstrapIcon = icon?.type === 'bootstrap' && /^bi-[a-z0-9-]+$/.test(icon.value || '')
    ? icon.value
    : 'bi-award';
  const iconNode = imageId
    ? element('img', {
      class: ['project-skill-icon'],
      'data-project-field': 'skill-icon',
      'data-managed-role': 'project-skill-icon',
      'data-image-id': imageId,
      src: imageSrc,
      alt: '',
      'aria-hidden': true,
    })
    : element('span', {
      class: ['project-skill-icon'],
      'data-project-field': 'skill-icon',
      'data-managed-role': 'project-skill-icon',
      'aria-hidden': true,
    }, [element('i', {
      class: ['bi', bootstrapIcon],
      'aria-hidden': true,
    })]);

  return element('span', {
    class: ['project-skill'],
    'data-project-field': 'skill',
    'data-project-skill-id': skillId,
    'data-managed-role': 'project-skill',
  }, [iconNode, text(skill?.name || 'Untitled skill')]);
};

export const createProjectItemNode = (
  project,
  imageDataById = new Map(),
  stableNodeId = '',
  { skills = [], skillIconImagesById = new Map() } = {}
) => {
  const projectId = normalizeRecordId(project?.id);
  const projectUrl = String(project?.projectUrl || project?.linkUrl || '').trim();
  const images = Array.isArray(project?.images) ? project.images : [];
  const carouselId = projectCarouselId(projectId, stableNodeId);
  const imageNodes = images.map((image, index) => {
    const imageId = normalizeRecordId(typeof image === 'string' ? image : image?.imageId || image?.id);
    return createProjectImageNode(image, imageDataById.get(imageId), index === 0);
  });
  const carouselChildren = [
    element('div', {
      class: ['carousel-inner', 'project-carousel-track'],
      'data-managed-role': 'project-carousel-track',
    }, imageNodes.length ? imageNodes : [
      element('div', {
        class: ['project-image-placeholder'],
        'data-managed-role': 'project-image-placeholder',
      }, [text('No project image')]),
    ]),
  ];
  if (imageNodes.length > 1) {
    carouselChildren.push(
      createProjectCarouselControl(carouselId, 'prev'),
      createProjectCarouselControl(carouselId, 'next')
    );
  }

  return element('div', {
    class: ['project-item'],
    'data-project-id': projectId,
    'data-managed-role': 'project-item',
    role: 'button',
    tabIndex: 0,
    'aria-label': `View details for ${project?.title || 'project'}`,
  }, [
    element('div', {
      id: carouselId,
      class: ['project-images', 'carousel', 'slide'],
      'data-managed-role': 'project-images',
      'data-bs-interval': 'false',
      'data-bs-touch': 'true',
    }, carouselChildren),
    element('h2', { class: ['project-title'], 'data-project-field': 'title' }, [text(project?.title || 'Untitled project')]),
    element('p', { class: ['project-overview'], 'data-project-field': 'overview' }, [text(project?.overview || project?.description || '')]),
    ...(skills.length ? [element('div', {
      class: ['project-skills'],
      'data-project-field': 'skills',
      'data-managed-role': 'project-skills',
      'aria-label': 'Skills used',
    }, skills.map((skill) => createProjectSkillNode(skill, skillIconImagesById)))] : []),
    element('a', {
      class: ['project-link'],
      href: projectUrl || '#',
      'data-project-field': 'project-url',
      ...(projectUrl ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
    }, [text(projectUrl ? 'View project' : 'Project link unavailable')]),
    element('span', {
      class: ['project-detail-hint'],
      'data-managed-role': 'project-detail-hint',
      'aria-hidden': true,
    }, [
      element('i', {
        class: ['bi', 'bi-arrows-fullscreen'],
        'aria-hidden': true,
        'data-managed-role': 'project-detail-icon',
      }),
      text('View details'),
    ]),
  ]);
};

export const createSkillItemNode = (skill) => {
  const rating = Math.max(0, Math.min(5, Number(skill?.rating) || 0));
  const icon = skill?.icon;
  const iconNode = icon?.type === 'image' && typeof icon.imageId === 'string' && icon.imageId.trim()
    ? element('img', {
      class: ['skill-icon'],
      'data-managed-role': 'skill-icon',
      'data-skill-field': 'icon',
      'data-image-id': icon.imageId.trim(),
      src: '',
      alt: `${skill?.name || 'Skill'} icon`,
    })
    : element('span', {
      class: ['skill-icon'],
      'data-managed-role': 'skill-icon',
      'data-skill-field': 'icon',
      role: 'img',
      'aria-label': `${skill?.name || 'Skill'} icon`,
    }, [element('i', {
      class: ['bi', icon?.type === 'bootstrap' && /^bi-[a-z0-9-]+$/.test(icon.value || '') ? icon.value : 'bi-award'],
      'aria-hidden': true,
    })]);

  return element('div', {
    class: ['skill-item', 'h-100'],
    'data-managed-role': 'skill-item-content',
  }, [
    iconNode,
    element('div', { class: ['skill-rating'], 'data-skill-field': 'rating', 'aria-label': `${rating} out of 5` },
      Array.from({ length: rating }, (_, index) => element('i', {
        class: ['bi', 'bi-star-fill'],
        'data-managed-role': 'rating-star',
        'data-rating-index': index,
        'aria-hidden': true,
      }))),
    element('h3', { class: ['skill-name'], 'data-skill-field': 'name' }, [text(skill?.name || 'Untitled skill')]),
    element('p', { class: ['skill-description'], 'data-skill-field': 'description' }, [text(skill?.description || '')]),
  ]);
};

const createSkillColumnNode = (skill) => element('div', {
  class: ['col-12', 'col-md-6', 'col-lg-4'],
  'data-skill-id': normalizeRecordId(skill?.id),
  'data-managed-role': 'skill-item',
}, [createSkillItemNode(skill)]);

export const createCollectionNode = (kind, items = []) => {
  const isProjects = kind === 'project' || kind === PROJECTS_TAG;
  const collection = element(isProjects ? PROJECTS_TAG : SKILLS_TAG, {
    class: [isProjects ? 'projects-collection' : 'skills-collection'],
    'data-managed-role': isProjects ? 'projects-collection' : 'skills-collection',
  }, []);
  return isProjects ? { ...collection, children: items } : replaceCollectionItems(collection, items);
};

const nodeIdentity = (node) => {
  if (!node || isTextNode(node)) return null;
  const attributes = node.attributes || {};
  const identity = attributes['data-project-id'] || attributes['data-skill-id'] ||
    attributes['data-project-skill-id'] || attributes['data-image-id'] ||
    attributes['data-project-field'] || attributes['data-skill-field'] || attributes['data-managed-role'];
  const index = attributes['data-rating-index'];
  const direction = attributes['data-bs-slide'];
  return identity ? `${node.type}:${identity}:${index ?? ''}:${direction ?? ''}` : null;
};

const mergeLiveNode = (previous, fresh) => {
  if (!previous || !fresh || previous.type !== fresh.type) return fresh;
  if (isTextNode(fresh)) return { ...fresh, id: previous.id || fresh.id };

  const previousChildren = Array.isArray(previous.children) ? previous.children : [];
  const usedIndexes = new Set();
  const children = (fresh.children || []).map((freshChild, freshIndex) => {
    const identity = nodeIdentity(freshChild);
    let previousIndex = identity
      ? previousChildren.findIndex((candidate, index) => !usedIndexes.has(index) && nodeIdentity(candidate) === identity)
      : freshIndex < previousChildren.length && !usedIndexes.has(freshIndex) && previousChildren[freshIndex]?.type === freshChild.type
        ? freshIndex
        : -1;

    if (previousIndex < 0 && isTextNode(freshChild)) {
      previousIndex = previousChildren.findIndex((candidate, index) => !usedIndexes.has(index) && isTextNode(candidate));
    }
    if (previousIndex >= 0) usedIndexes.add(previousIndex);
    return mergeLiveNode(previousChildren[previousIndex], freshChild);
  });

  const previousClasses = normalizeClassList(previous.attributes?.class)
    .filter((className) => !(
      fresh.attributes?.['data-managed-role'] === 'project-image'
      && className === 'active'
    ));
  const mergedClasses = normalizeClassList([
    ...(fresh.attributes?.class || []),
    ...previousClasses,
  ]);
  return {
    ...fresh,
    id: previous.id || fresh.id,
    attributes: {
      ...(previous.attributes || {}),
      ...(fresh.attributes || {}),
      ...(mergedClasses.length ? { class: mergedClasses } : {}),
    },
    children,
  };
};

const loadProjectImages = async (userId, project) => {
  const images = Array.isArray(project?.images) ? project.images : [];
  const entries = await Promise.all(images.map(async (image) => {
    const imageId = normalizeRecordId(typeof image === 'string' ? image : image?.imageId || image?.id);
    if (!imageId || !userId) return [imageId, null];
    try {
      return [imageId, await getImage(userId, imageId)];
    } catch (_) {
      return [imageId, null];
    }
  }));
  return new Map(entries);
};

const loadProjectSkills = async (
  userId,
  project,
  skillLoader = loadSkill,
  iconImageLoader = getImage
) => {
  const skills = (await Promise.all(projectSkillIds(project).map(async (skillId) => {
    try {
      return await skillLoader(userId, skillId);
    } catch (_) {
      return null;
    }
  }))).filter(Boolean);
  const imageIds = [...new Set(skills
    .map((skill) => skill?.icon?.type === 'image' ? normalizeRecordId(skill.icon.imageId) : '')
    .filter(Boolean))];
  const imageEntries = await Promise.all(imageIds.map(async (imageId) => {
    try {
      return [imageId, await iconImageLoader(userId, imageId)];
    } catch (_) {
      return [imageId, null];
    }
  }));

  return { skills, skillIconImagesById: new Map(imageEntries) };
};

const normalizePreviousSkillColumn = (node) => {
  if (!node || node.attributes?.['data-managed-role'] !== 'skill-item') return node;
  const classes = normalizeClassList(node.attributes?.class);
  if (!classes.includes('skill-item')) return node;

  const contentAttributes = { ...(node.attributes || {}) };
  delete contentAttributes['data-skill-id'];
  delete contentAttributes['data-record-unavailable'];
  contentAttributes.class = normalizeClassList([...classes, 'h-100']);
  contentAttributes['data-managed-role'] = 'skill-item-content';
  const content = {
    ...node,
    id: element('div').id,
    attributes: contentAttributes,
  };

  return {
    ...element('div', {
      class: ['col-12', 'col-md-6', 'col-lg-4'],
      'data-skill-id': getBoundRecordId(node, 'skill'),
      'data-managed-role': 'skill-item',
      ...(node.attributes?.['data-record-unavailable'] ? { 'data-record-unavailable': 'true' } : {}),
    }, [content]),
    id: node.id,
  };
};

const normalizePreviousProjectItem = (node) => {
  if (!node || node.attributes?.['data-managed-role'] !== 'project-item') return node;
  const imagesIndex = (node.children || []).findIndex(
    (child) => child.attributes?.['data-managed-role'] === 'project-images'
  );
  if (imagesIndex < 0) return node;

  const images = node.children[imagesIndex];
  const hasCarouselTrack = (images.children || []).some(
    (child) => child.attributes?.['data-managed-role'] === 'project-carousel-track'
  );
  if (hasCarouselTrack) return node;

  const projectId = getBoundRecordId(node, 'project');
  const normalizedFigures = (images.children || []).map((child, index) => ({
    ...child,
    attributes: {
      ...(child.attributes || {}),
      class: normalizeClassList([
        ...(child.attributes?.class || []),
        'carousel-item',
        index === 0 ? 'active' : '',
      ]),
    },
  }));
  const track = element('div', {
    class: ['carousel-inner', 'project-carousel-track'],
    'data-managed-role': 'project-carousel-track',
  }, normalizedFigures);
  const nextImages = {
    ...images,
    attributes: {
      ...(images.attributes || {}),
      id: projectCarouselId(projectId, node.id),
      class: normalizeClassList([...(images.attributes?.class || []), 'carousel', 'slide']),
      'data-bs-interval': 'false',
      'data-bs-touch': 'true',
    },
    children: [track],
  };
  const children = [...node.children];
  children[imagesIndex] = nextImages;
  return { ...node, children };
};

export const hydrateCollectionNode = async (node, userId, loaders = {}) => {
  if (!isCollectionNode(node) || !userId) return node;

  const kind = getCollectionKind(node);
  const recordIds = getCollectionRecordIds(node);
  const projectLoader = loaders.loadProject || loadProject;
  const skillLoader = loaders.loadSkill || loadSkill;
  const imageLoader = loaders.loadProjectImages || loadProjectImages;
  const previousById = new Map(getCollectionItems(node).map((child) => {
    const previous = kind === 'skill'
      ? normalizePreviousSkillColumn(child)
      : normalizePreviousProjectItem(child);
    return [getBoundRecordId(previous, kind), previous];
  }));

  const children = await Promise.all(recordIds.map(async (recordId) => {
    const previous = previousById.get(recordId);
    try {
      const record = kind === 'project'
        ? await projectLoader(userId, recordId)
        : await skillLoader(userId, recordId);
      if (!record) {
        return {
          ...previous,
          attributes: { ...(previous?.attributes || {}), 'data-record-unavailable': 'true' },
        };
      }

      let fresh;
      if (kind === 'project') {
        const [projectImages, projectSkills] = await Promise.all([
          imageLoader(userId, record),
          (loaders.loadProjectSkills || loadProjectSkills)(
            userId,
            record,
            skillLoader,
            loaders.loadSkillIconImage || getImage
          ),
        ]);
        fresh = createProjectItemNode(
          record,
          projectImages,
          previous?.id || recordId,
          projectSkills
        );
      } else {
        fresh = createSkillColumnNode(record);
      }
      const merged = mergeLiveNode(previous, fresh);
      const attributes = { ...(merged.attributes || {}) };
      delete attributes['data-record-unavailable'];
      return { ...merged, attributes };
    } catch (_) {
      return previous ? {
        ...previous,
        attributes: { ...(previous.attributes || {}), 'data-record-unavailable': 'true' },
      } : null;
    }
  }));

  return replaceCollectionItems(node, children.filter(Boolean));
};

const hydrateTree = async (node, userId, loaders) => {
  if (!node || isTextNode(node)) return node;
  if (isCollectionNode(node)) return hydrateCollectionNode(node, userId, loaders);

  return {
    ...node,
    children: await Promise.all((node.children || []).map((child) => hydrateTree(child, userId, loaders))),
  };
};

export const mergeCollectionClassDefinitions = (classes = {}) => {
  const mergedClasses = cloneEditorValue(classes || {});

  Object.entries(COLLECTION_CLASS_DEFINITIONS).forEach(([className, defaultDefinition]) => {
    const existingDefinition = mergedClasses[className] || {};
    mergedClasses[className] = ['base', 'sm', 'md', 'lg', 'xl'].reduce((definition, breakpoint) => {
      const existingStyles = existingDefinition[breakpoint] || {};
      const mergedStyles = {
        ...(defaultDefinition[breakpoint] || {}),
        ...existingStyles,
      };
      if (breakpoint === 'base') {
        Object.entries(LEGACY_PROJECT_BASE_VALUES[className] || {}).forEach(([property, legacyValue]) => {
          if (existingStyles[property] === legacyValue) {
            mergedStyles[property] = defaultDefinition.base[property];
          }
        });
      }
      definition[breakpoint] = mergedStyles;
      return definition;
    }, {});
  });

  return mergedClasses;
};

export const hydrateEditorDocumentCollections = async (editorDocument, userId, loaders = {}) => {
  const current = validateEditorDocument(editorDocument).document;
  const hasCollections = containsCollectionNode(current.root);
  return validateEditorDocument({
    ...current,
    root: await hydrateTree(current.root, userId, loaders),
    classes: hasCollections ? mergeCollectionClassDefinitions(current.classes) : current.classes,
  }).document;
};

export const createHydratedCollection = async (kind, recordIds, userId, loaders = {}) => {
  const uniqueIds = [...new Set((recordIds || []).map(normalizeRecordId).filter(Boolean))];
  const placeholders = uniqueIds.map((recordId) => element('div', {
    [`data-${kind}-id`]: recordId,
    'data-managed-role': `${kind}-item`,
  }));
  return hydrateCollectionNode(createCollectionNode(kind, placeholders), userId, loaders);
};

export const updateCollectionSelection = async (node, recordIds, userId, loaders = {}) => {
  if (!isCollectionNode(node)) return node;
  const kind = getCollectionKind(node);
  const uniqueIds = [...new Set((recordIds || []).map(normalizeRecordId).filter(Boolean))];
  const existingById = new Map(getCollectionItems(node).map((child) => [getBoundRecordId(child, kind), child]));
  const items = uniqueIds.map((recordId) => existingById.get(recordId) || element('div', {
      [`data-${kind}-id`]: recordId,
      'data-managed-role': `${kind}-item`,
    }));
  const nextNode = replaceCollectionItems(node, items);
  return hydrateCollectionNode(nextNode, userId, loaders);
};

const allowedIdSet = (values) => Array.isArray(values) ? new Set(values.map(normalizeRecordId).filter(Boolean)) : null;

export const validateCollectionBindings = (editorDocument, { projectIds, skillIds } = {}) => {
  const safe = validateEditorDocument(editorDocument).document;
  const allowedProjects = allowedIdSet(projectIds);
  const allowedSkills = allowedIdSet(skillIds);

  const visit = (node, collectionKind = null, isDirectItem = false) => {
    if (!node || isTextNode(node)) return node;
    const kind = getCollectionKind(node) || collectionKind;
    const attributes = { ...(node.attributes || {}) };

    if (!kind) {
      ['data-project-id', 'data-skill-id', 'data-project-field', 'data-skill-field', 'data-managed-role']
        .forEach((name) => delete attributes[name]);
    }

    if (kind) {
      const fieldName = attributes[`data-${kind}-field`];
      const allowedFields = kind === 'project' ? PROJECT_FIELDS : SKILL_FIELDS;
      if (fieldName && !allowedFields.has(String(fieldName))) {
        delete attributes[`data-${kind}-field`];
      }
      delete attributes[`data-${kind === 'project' ? 'skill' : 'project'}-field`];
      delete attributes[`data-${kind === 'project' ? 'skill' : 'project'}-id`];
      if (kind === 'project' && attributes['data-project-skill-id']) {
        const projectSkillId = normalizeRecordId(attributes['data-project-skill-id']);
        if (fieldName !== 'skill' || (allowedSkills && !allowedSkills.has(projectSkillId))) {
          delete attributes['data-project-skill-id'];
        }
      } else {
        delete attributes['data-project-skill-id'];
      }
      if (!isDirectItem) {
        delete attributes[`data-${kind}-id`];
      }
      if (attributes['data-managed-role'] && !MANAGED_ROLES.has(String(attributes['data-managed-role']))) {
        delete attributes['data-managed-role'];
      }
    }

    if (isCollectionNode(node)) {
      delete attributes['data-project-id'];
      delete attributes['data-skill-id'];
      const allowed = node.type === PROJECTS_TAG ? allowedProjects : allowedSkills;
      const childKind = getCollectionKind(node);
      const children = getCollectionItems(node)
        .filter((child) => child?.type === 'div')
        .filter((child) => {
          const recordId = getBoundRecordId(child, childKind);
          return recordId && (!allowed || allowed.has(recordId));
        })
        .map((child) => visit(child, childKind, true));
      return replaceCollectionItems({ ...node, attributes }, children);
    }

    return {
      ...node,
      attributes,
      children: (node.children || []).map((child) => visit(child, kind)).filter(Boolean),
    };
  };

  return validateEditorDocument({ ...safe, root: visit(safe.root) }).document;
};
